/****************************************************
 * Module Name:  stoneTypeMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import StoneTypeProxy from "../../utility/proxy/manager/stoneTypeProxy";
import AddStoneTypeModal from "./addStoneTypeModal";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import PageTitle from "../common/pageTitle";

export class StoneTypeMaster extends Component {
    constructor(props) {
        super(props);

        this.state = {

            stonetypes: [],
            showAddStoneTypeModal: false,
            accesDenied: false,
            editOperation: false,
            sourceData: {},
            errorSnackbar: false,
            successSnackbar: false,
            snackbarMessage: "",
            loadingButton: false,
            showStoneTypeLoadSpinner: true,
            local_Permission: Constants.USER_PERMISSION.filter(
                (iPermission) =>
                    iPermission.permissionid === Constants.PERMISSIONS.MASTER_STONETYPE
            ),
        };

        this.checkPermission()
    }

    componentDidMount() {
        PageTitle("MASTER_STONETYPE");
    }

    checkPermission = () => {
        let allowed = Utility.isReadAllowed(this.state.local_Permission)
        if (!allowed) {
            this.setState({
                accesDenied: true
            })
        } else {
            this.fetchStoneTypeData(false);
        }
    }


    fetchStoneTypeData = (bAnimateSpinner) => {
        bAnimateSpinner && this.setState({ showStoneTypeLoadSpinner: true });
        StoneTypeProxy.GetAllStoneType((responseData) => {
            if (responseData) {
                this.setState({ stonetypes: responseData, showStoneTypeLoadSpinner: false })
            } else {
                this.setState({ errorSnackbar: true, showStoneTypeLoadSpinner: false })
            }
        });
    };

    columns = [
        {
            field: "name",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 200,
            renderCell: renderCellExpand,
        },
        {
            field: "fullname",
            headerName: "FULL NAME",
            headerAlign: "center",
            align: "center",
            width: 370,
            renderCell: renderCellExpand,
        },
        {
            field: "hscode",
            headerName: "HS CODE",
            headerAlign: "center",
            align: "center",
            width: 370,
            renderCell: renderCellExpand,
        },
        {
            field: "seq",
            headerName: "SEQUENCE",
            headerAlign: "center",
            align: "center",
            width: 170,
            renderCell: renderCellExpand,
        },
        {
            field: "disabled",
            headerName: "DISABLED",
            headerAlign: "center",
            align: "center",
            width: 370,
            renderCell: (params) =>
                params.row.disabled ? (
                    <CheckIcon color="success" />
                ) : (
                    <ClearIcon color="error" />
                ),
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            width: 320,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            this.setState({
                                showAddStoneTypeModal: true,
                                editOperation: true,
                                sourceData: params.row,
                                loadingButton: false,
                            });
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    handleValidation = (stoneTypeData) => {
        //TODO do validation if needed here
        return true;
    };

    addStoneType = (stoneTypeData) => {
        this.setState({ loadingButton: true });
        if (this.state.editOperation) {
            //Updating Stone Type, and once stonetype is updated on call refresh the list of current StoneTypes
            StoneTypeProxy.UpdateStoneType(stoneTypeData, (response) => {
                if (response) {
                    this.fetchStoneTypeData(true);
                    this.setState({ loadingButton: false, showAddStoneTypeModal: false, successSnackbar: true, snackbarMessage: "Stone Type updated successfully." });
                } else {
                    this.setState({ errorSnackbar: true });
                    this.setState({ loadingButton: false });
                }
            });
        } else {
            //Creating new Stone Type, and once Stone Type is created on call refresh the list of current stonetypes
            StoneTypeProxy.AddStoneType(stoneTypeData, (response) => {
                if (response) {
                    this.fetchStoneTypeData(true);
                    this.setState({ loadingButton: false, showAddStoneTypeModal: false, successSnackbar: true, snackbarMessage: "Stone Type added successfully." });
                } else {
                    this.setState({ errorSnackbar: true });
                    this.setState({ loadingButton: false });
                }
            });
        }
    };

    render() {
        const CustomToolbar = () => {
            return (
                <GridToolbarContainer
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        marginHorizontal: "5px",
                        "&.MuiDataGrid-toolbarContainer": {
                            padding: "0px 4px !important",
                        },
                    }}
                >
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarExport />
                    <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                    {this.state.showStoneTypeLoadSpinner ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                color="primary"
                                fontSize="medium"
                                onClick={() => this.fetchStoneTypeData(true)}
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}
                    {Utility.isAddAllowed(this.state.local_Permission) ? (
                        <Tooltip title="Add Stone Type">
                            <Button
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{ ml: "8px" }}
                                onClick={() =>
                                    this.setState({
                                        showAddStoneTypeModal: true,
                                        editOperation: false,
                                        sourceData: {},
                                    })
                                }
                                startIcon={<AddBoxOutlinedIcon size="large" />}
                            >
                                Stone Type
                            </Button>
                        </Tooltip>
                    ) : null}
                    <GridToolbarQuickFilter debounceMs={500} />
                </GridToolbarContainer>
            );
        }
        let allowed = Utility.isReadAllowed(this.state.local_Permission)
        if (!allowed) {
            return <NoPermission />
        }
        const rows = this.state.stonetypes;
        const columns = Utility.isUpdateAllowed(this.state.local_Permission)
            ? this.columns
            : this.columns.filter((col) => col.field !== "edit");

        return (
            <Box>
                <CommonSnackbar
                    show={this.state.errorSnackbar}
                    message={<Box>{Constants.APiFailErrorMessage}</Box>}
                    variant={"error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    handleClose={() => this.setState({ errorSnackbar: false })}
                    title="Failed"
                />

                <CommonSnackbar
                    show={this.state.successSnackbar}
                    message={this.state.snackbarMessage}
                    variant={"success"}
                    autoHide={Constants.SnackBarCloseTimer}
                    handleClose={() => this.setState({ successSnackbar: false })}
                    title="Success"
                />

                <br />

                {this.state.showAddStoneTypeModal ? (
                    <AddStoneTypeModal
                        Show={true}
                        handleClose={() => this.setState({ showAddStoneTypeModal: false })}
                        handleSave={this.addStoneType}
                        handleValidation={this.handleValidation}
                        editOperation={this.state.editOperation}
                        sourceData={this.state.sourceData}
                        loading={this.state.loadingButton}
                    />
                ) : (
                    <Box />
                )}
                <Box px={1}>
                    <Box
                        sx={{
                            width: "100%",
                            backgroundColor: '#fff',
                            color: '#000',
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "85vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            getRowHeight={() => "auto"}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },
                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            slots={{ toolbar: CustomToolbar }}
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => row.name}
                            loading={this.state.showStoneTypeLoadSpinner}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns status and traderName, the other columns will remain visible
                                        id: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default StoneTypeMaster;
